/**
 * Builds a real, openable, visually-styled PDF entirely client-side — for mock mode only.
 *
 * The real backend renders this exact design server-side (fpdf2) and stores it on S3;
 * pointing the mock at a fake S3 path would 403/AccessDenied the moment "Aperçu" is
 * clicked (nothing was ever uploaded there). Drawing the same layout by hand in raw PDF
 * operators sidesteps that — no library, no backend, just a `blob:` URL the browser's
 * own viewer opens directly — while still looking like the real, validated report design
 * rather than a bare page of text.
 *
 * Text must stay ASCII: nothing here declares an /Encoding beyond the standard fonts'
 * default, so a raw UTF-8 accented character would render as mojibake. `stripDiacritics`
 * normalizes "Léonidas" -> "Leonidas" before anything is drawn.
 */

export type RgbColor = readonly [number, number, number];

const PAGE_W = 595;
const PAGE_H = 842;
const MARGIN = 40;
const CONTENT_W = PAGE_W - 2 * MARGIN;

const DARK_BG: RgbColor = [26, 36, 51];
const PERIOD_BAR_BG: RgbColor = [36, 48, 66];
const GOLD: RgbColor = [232, 160, 32];
const BLUE: RgbColor = [59, 130, 246];
const ORANGE: RgbColor = [249, 115, 22];
const ORANGE_BG: RgbColor = [255, 247, 237];
const CARD_BG: RgbColor = [248, 250, 252];
const BORDER_GREY: RgbColor = [226, 232, 240];
const TEXT_DARK: RgbColor = [26, 36, 51];
const TEXT_LIGHT: RgbColor = [255, 255, 255];
const TEXT_MUTED: RgbColor = [100, 116, 139];
const HEADER_MUTED: RgbColor = [148, 163, 184];

export interface MockSeoKpi {
  value: string;
  label: string;
  color: RgbColor;
}

export interface MockSeoFiguresRow {
  label: string;
  value: string;
  meaning: string;
}

export interface MockSeoKeywordRow {
  keyword: string;
  position: string;
  status: string;
  statusColor: RgbColor;
  statusBg: RgbColor;
}

export interface MockSeoAction {
  title: string;
  objective: string;
  bullets: string[];
  impact: string;
  color: RgbColor;
}

export interface MockSeoReportData {
  businessName: string;
  businessAddress: string;
  periodLabel: string;
  monthBadge: string;
  kpis: MockSeoKpi[];
  insightHeadline: string;
  insightBody: string;
  figuresRows: MockSeoFiguresRow[];
  keywordRows: MockSeoKeywordRow[];
  attentionText: string;
  actions: MockSeoAction[];
  footerNote: string;
}

const stripDiacritics = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[–—]/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[^\x00-\x7F]/g, '-');
const escapePdfText = (text: string) => text.replace(/([()\\])/g, '\\$1');
const pdfText = (text: string) => escapePdfText(stripDiacritics(text));
const rgbOp = (color: RgbColor) => color.map(channel => (channel / 255).toFixed(3)).join(' ');

const CHAR_WIDTH_FACTOR = { regular: 0.5, bold: 0.56 };
const textWidth = (text: string, size: number, bold: boolean) =>
  text.length * size * (bold ? CHAR_WIDTH_FACTOR.bold : CHAR_WIDTH_FACTOR.regular);

const wrapText = (text: string, maxWidth: number, size: number, bold: boolean): string[] => {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && textWidth(candidate, size, bold) > maxWidth) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [''];
};

/** One page's worth of PDF operators, plus the vertical cursor ("distance from the top,
 * in points") every draw call advances. */
class MockPdfPage {
  private readonly ops: string[] = [];
  y = MARGIN;

  private push(op: string) {
    this.ops.push(op);
  }

  fillRect(x: number, topY: number, w: number, h: number, color: RgbColor) {
    this.push(`${rgbOp(color)} rg`);
    this.push(`${x} ${PAGE_H - topY - h} ${w} ${h} re f`);
  }

  strokeRect(x: number, topY: number, w: number, h: number, color: RgbColor, lineWidth = 1) {
    this.push(`${rgbOp(color)} RG`);
    this.push(`${lineWidth} w`);
    this.push(`${x} ${PAGE_H - topY - h} ${w} ${h} re S`);
  }

  text(x: number, topY: number, size: number, color: RgbColor, str: string, bold = false) {
    const font = bold ? '/F2' : '/F1';
    this.push(
      `BT ${font} ${size} Tf ${rgbOp(color)} rg ${x.toFixed(2)} ${(PAGE_H - topY).toFixed(2)} Td (${pdfText(str)}) Tj ET`,
    );
  }

  centeredText(x: number, w: number, topY: number, size: number, color: RgbColor, str: string, bold = false) {
    const offset = Math.max(0, (w - textWidth(str, size, bold)) / 2);
    this.text(x + offset, topY, size, color, str, bold);
  }

  /** Wraps and draws a paragraph; returns the vertical space it used. */
  wrapped(
    x: number,
    topY: number,
    maxWidth: number,
    size: number,
    color: RgbColor,
    str: string,
    bold = false,
    lineHeight = size * 1.35,
  ): number {
    const lines = wrapText(str, maxWidth, size, bold);
    lines.forEach((line, index) => this.text(x, topY + index * lineHeight, size, color, line, bold));
    return lines.length * lineHeight;
  }

  wrappedLineCount(maxWidth: number, size: number, str: string, bold = false): number {
    return wrapText(str, maxWidth, size, bold).length;
  }

  content(): string {
    return `${this.ops.join('\n')}\n`;
  }
}

class MockPdfDocument {
  readonly pages: MockPdfPage[] = [];
  current: MockPdfPage;

  constructor() {
    this.current = new MockPdfPage();
    this.pages.push(this.current);
  }

  newPage() {
    this.current = new MockPdfPage();
    this.pages.push(this.current);
  }

  /** Starts a fresh page if what comes next would run past the bottom margin. */
  ensureSpace(neededHeight: number) {
    if (this.current.y + neededHeight > PAGE_H - MARGIN) this.newPage();
  }
}

const drawFooter = (page: MockPdfPage, footerNote: string) => {
  page.strokeRect(MARGIN, PAGE_H - MARGIN - 20, CONTENT_W, 0, BORDER_GREY, 0.5);
  page.text(MARGIN, PAGE_H - MARGIN - 14, 7, HEADER_MUTED, footerNote);
};

const drawHeader = (doc: MockPdfDocument, data: MockSeoReportData) => {
  const page = doc.current;
  const headerHeight = 110;
  page.fillRect(0, 0, PAGE_W, headerHeight, DARK_BG);

  page.text(MARGIN, 24, 8, HEADER_MUTED, 'LITTLEBILL - RAPPORT SEO LOCAL (MAQUETTE)');
  page.text(MARGIN, 48, 22, TEXT_LIGHT, data.businessName, true);
  page.text(MARGIN, 66, 9, HEADER_MUTED, data.businessAddress);

  const badgeW = 68;
  const badgeH = 26;
  page.fillRect(PAGE_W - MARGIN - badgeW, 18, badgeW, badgeH, GOLD);
  page.centeredText(PAGE_W - MARGIN - badgeW, badgeW, 34, 10, TEXT_DARK, data.monthBadge, true);

  page.fillRect(MARGIN, 80, CONTENT_W, 20, PERIOD_BAR_BG);
  page.text(
    MARGIN + 6,
    93,
    8,
    HEADER_MUTED,
    `Analyse de performance - ${data.periodLabel} - Donnees Uberall / SEO local`,
  );

  page.y = headerHeight + 16;
};

const drawIntro = (doc: MockPdfDocument, data: MockSeoReportData) => {
  const page = doc.current;
  const used = page.wrapped(
    MARGIN,
    page.y,
    CONTENT_W,
    9,
    TEXT_DARK,
    `Ce rapport presente les resultats obtenus grace a votre strategie de referencement ` +
      `local sur Google. Le classement des mots-cles couvre ${data.periodLabel}. Vous ` +
      `trouverez ici l'evolution de vos classements, une analyse de votre portefeuille ` +
      `de mots-cles et un plan d'action concret.`,
  );
  page.y += used + 10;
};

const drawKpiCards = (doc: MockPdfDocument, data: MockSeoReportData) => {
  const page = doc.current;
  const perRow = Math.min(data.kpis.length, 3) || 1;
  const gap = 8;
  const cardW = (CONTENT_W - (perRow - 1) * gap) / perRow;
  const cardH = 46;

  data.kpis.forEach((kpi, index) => {
    const row = Math.floor(index / perRow);
    const col = index % perRow;
    const x = MARGIN + col * (cardW + gap);
    const top = page.y + row * (cardH + gap);
    page.fillRect(x, top, cardW, cardH, CARD_BG);
    page.centeredText(x, cardW, top + 22, 17, kpi.color, kpi.value, true);
    page.centeredText(x, cardW, top + 36, 7, TEXT_MUTED, kpi.label.slice(0, 40));
  });

  const rows = Math.ceil(data.kpis.length / perRow);
  page.y += rows * (cardH + gap) + 8;
};

const drawInsightBox = (doc: MockPdfDocument, data: MockSeoReportData) => {
  const page = doc.current;
  const headlineLines = page.wrappedLineCount(CONTENT_W - 8, 9, data.insightHeadline, true);
  const bodyLines = page.wrappedLineCount(CONTENT_W - 8, 9, data.insightBody);
  const height = (headlineLines + bodyLines) * 9 * 1.35 + 24;

  page.fillRect(MARGIN, page.y, CONTENT_W, height, ORANGE_BG);
  page.strokeRect(MARGIN, page.y, CONTENT_W, height, ORANGE, 1);
  page.text(MARGIN + 6, page.y + 14, 8, ORANGE, 'ANALYSE DE PERFORMANCE', true);

  const headlineUsed = page.wrapped(MARGIN + 6, page.y + 26, CONTENT_W - 12, 9, TEXT_DARK, data.insightHeadline, true);
  page.wrapped(MARGIN + 6, page.y + 26 + headlineUsed, CONTENT_W - 12, 9, TEXT_DARK, data.insightBody);

  page.y += height + 14;
};

const drawSectionTitle = (doc: MockPdfDocument, title: string) => {
  const page = doc.current;
  doc.ensureSpace(24);
  page.text(MARGIN, doc.current.y, 12, TEXT_DARK, title, true);
  doc.current.y += 18;
};

const drawFiguresTable = (doc: MockPdfDocument, data: MockSeoReportData) => {
  drawSectionTitle(doc, 'Votre visibilite Google en chiffres');
  const page = doc.current;
  const widths = [140, 70, CONTENT_W - 210];
  const rowH = 20;

  doc.ensureSpace(rowH);
  page.fillRect(MARGIN, page.y, CONTENT_W, rowH, DARK_BG);
  const heads = ['Indicateur', 'Resultat', 'Signification'];
  let x = MARGIN;
  heads.forEach((head, index) => {
    page.text(x + 4, page.y + 13, 8, TEXT_LIGHT, head, true);
    x += widths[index];
  });
  page.y += rowH;

  data.figuresRows.forEach((row, index) => {
    doc.ensureSpace(rowH);
    page.fillRect(MARGIN, page.y, CONTENT_W, rowH, index % 2 === 0 ? CARD_BG : TEXT_LIGHT);
    page.text(MARGIN + 4, page.y + 13, 8, TEXT_DARK, row.label);
    page.text(MARGIN + widths[0] + 4, page.y + 13, 8.5, BLUE, row.value, true);
    page.text(MARGIN + widths[0] + widths[1] + 4, page.y + 13, 7.5, TEXT_MUTED, row.meaning.slice(0, 55));
    page.y += rowH;
  });
  page.y += 12;
};

const drawKeywordsTable = (doc: MockPdfDocument, data: MockSeoReportData) => {
  drawSectionTitle(doc, `Classement complet des mots-cles - ${data.periodLabel}`);
  const page = doc.current;
  const widths = [CONTENT_W - 200, 60, 140];
  const rowH = 20;

  doc.ensureSpace(rowH);
  page.fillRect(MARGIN, page.y, CONTENT_W, rowH, DARK_BG);
  page.text(MARGIN + 4, page.y + 13, 8, TEXT_LIGHT, 'Mot-cle', true);
  page.text(MARGIN + widths[0] + 4, page.y + 13, 8, TEXT_LIGHT, 'Position', true);
  page.text(MARGIN + widths[0] + widths[1] + 4, page.y + 13, 8, TEXT_LIGHT, 'Statut', true);
  page.y += rowH;

  data.keywordRows.forEach((row, index) => {
    doc.ensureSpace(rowH);
    page.fillRect(MARGIN, page.y, CONTENT_W, rowH, index % 2 === 0 ? CARD_BG : TEXT_LIGHT);
    page.text(MARGIN + 4, page.y + 13, 8, TEXT_DARK, row.keyword.slice(0, 42));
    page.text(MARGIN + widths[0] + 4, page.y + 13, 8, TEXT_MUTED, row.position);

    const chipX = MARGIN + widths[0] + widths[1] + 4;
    const chipW = Math.min(110, textWidth(row.status, 7, true) + 12);
    page.fillRect(chipX, page.y + 3, chipW, 14, row.statusBg);
    page.centeredText(chipX, chipW, page.y + 13, 7, row.statusColor, row.status, true);
    page.y += rowH;
  });

  page.y += 6;
  const footnote =
    "Les positions sont une moyenne indicative arrondie a l'entier le plus proche. " +
    '"-" signifie que le mot-cle est hors du Top 60 sur la periode.';
  page.y += page.wrapped(MARGIN, page.y, CONTENT_W, 6.5, TEXT_MUTED, footnote) + 10;
};

const drawAttentionBox = (doc: MockPdfDocument, data: MockSeoReportData) => {
  const page = doc.current;
  const lines = page.wrappedLineCount(CONTENT_W - 12, 8, data.attentionText);
  const height = lines * 8 * 1.35 + 18;
  doc.ensureSpace(height + 6);

  page.fillRect(MARGIN, page.y, CONTENT_W, height, CARD_BG);
  page.strokeRect(MARGIN, page.y, CONTENT_W, height, BLUE, 1);
  page.text(MARGIN + 6, page.y + 13, 8, BLUE, "Points d'attention detectes", true);
  page.wrapped(MARGIN + 6, page.y + 24, CONTENT_W - 12, 8, TEXT_DARK, data.attentionText);
  page.y += height + 14;
};

const drawActionPlan = (doc: MockPdfDocument, data: MockSeoReportData) => {
  drawSectionTitle(doc, "Plan d'action - 3 priorites pour les prochains mois");

  data.actions.forEach((action, index) => {
    const bulletLines = action.bullets.length;
    const height = 46 + bulletLines * 11;
    doc.ensureSpace(height + 10);
    const page = doc.current;
    const top = page.y;

    page.fillRect(MARGIN, top, CONTENT_W, height, CARD_BG);
    page.strokeRect(MARGIN, top, CONTENT_W, height, action.color, 1.2);

    page.fillRect(MARGIN + 6, top + 8, 16, 16, action.color);
    page.centeredText(MARGIN + 6, 16, top + 19, 9, TEXT_LIGHT, String(index + 1), true);

    page.text(MARGIN + 28, top + 18, 9.5, TEXT_DARK, action.title.slice(0, 70), true);
    page.text(MARGIN + 28, top + 30, 7.5, TEXT_MUTED, `Objectif : ${action.objective}`.slice(0, 100));

    let y = top + 42;
    action.bullets.forEach(bullet => {
      page.text(MARGIN + 30, y, 7.5, TEXT_DARK, `- ${bullet}`.slice(0, 100));
      y += 11;
    });

    page.text(MARGIN + 28, top + height - 8, 7.5, action.color, `Impact estime : ${action.impact}`.slice(0, 100), true);
    page.y = top + height + 10;
  });
};

/** All pages' finished content streams for the mock SEO report — the drawing pipeline,
 * separated from PDF byte assembly so the layout is testable without touching bytes. */
export const buildMockSeoReportPages = (data: MockSeoReportData): string[] => {
  const doc = new MockPdfDocument();

  drawHeader(doc, data);
  drawIntro(doc, data);
  drawKpiCards(doc, data);
  drawInsightBox(doc, data);
  drawFiguresTable(doc, data);
  drawKeywordsTable(doc, data);
  drawAttentionBox(doc, data);
  drawActionPlan(doc, data);

  doc.pages.forEach(page => drawFooter(page, data.footerNote));
  return doc.pages.map(page => page.content());
};

/** Assembles finished page content streams into real PDF bytes: one Page + one Content
 * object per page, two shared fonts (regular/bold), a Pages tree, a Catalog and a
 * correctly-offset xref table. Split out from the drawing pipeline above so the byte
 * structure (offsets, object count) is testable independently of what got drawn. */
export const assembleMockPdfBytes = (pageContents: string[]): Uint8Array => {
  const encoder = new TextEncoder();
  const pageCount = pageContents.length;

  // 1 Catalog, 2 Pages, 3 Font regular, 4 Font bold, then per page: N Page + N Content.
  const pagesFirstObj = 5;
  const contentFirstObj = pagesFirstObj + pageCount;
  const pageRefs = Array.from({ length: pageCount }, (_, i) => `${pagesFirstObj + i} 0 R`).join(' ');

  const objects: string[] = [
    `1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n`,
    `2 0 obj\n<< /Type /Pages /Kids [${pageRefs}] /Count ${pageCount} >>\nendobj\n`,
    `3 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n`,
    `4 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj\n`,
  ];

  pageContents.forEach((_, index) => {
    const pageObj = pagesFirstObj + index;
    const contentObj = contentFirstObj + index;
    objects.push(
      `${pageObj} 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] ` +
        `/Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentObj} 0 R >>\nendobj\n`,
    );
  });

  pageContents.forEach((content, index) => {
    const contentObj = contentFirstObj + index;
    const bytes = encoder.encode(content).length;
    objects.push(`${contentObj} 0 obj\n<< /Length ${bytes} >>\nstream\n${content}endstream\nendobj\n`);
  });

  let body = '%PDF-1.4\n';
  const offsets: number[] = [];
  for (const object of objects) {
    offsets.push(encoder.encode(body).length);
    body += object;
  }

  const xrefOffset = encoder.encode(body).length;
  const xrefEntries = offsets.map(offset => `${String(offset).padStart(10, '0')} 00000 n \n`).join('');
  const xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n${xrefEntries}`;
  const trailer = `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return encoder.encode(body + xref + trailer);
};

export const buildMockPdfBytes = (data: MockSeoReportData): Uint8Array =>
  assembleMockPdfBytes(buildMockSeoReportPages(data));

export const createMockPdfBlob = (data: MockSeoReportData): Blob =>
  new Blob([buildMockPdfBytes(data)], { type: 'application/pdf' });

export const createMockPdfBlobUrl = (data: MockSeoReportData): string => URL.createObjectURL(createMockPdfBlob(data));
